/**
 * Shared consent cookie writer + Consent Mode v2 applier.
 *
 * Used by both consent entry points so they write the `bizrnr_consent` cookie and
 * push the gtag consent update identically:
 *   - ConsentBanner       — region default + the EU/CA Accept/Reject banner.
 *   - PrivacyChoices       — the persistent footer opt-in / opt-out control.
 *
 * Keep in sync with `src/lib/consent/read.ts` (the reader) and
 * `src/lib/consent/regions.ts` (the decision logic).
 */

import { CONSENT_COOKIE } from './read';
import { asStoredConsent, type StoredConsent } from './regions';

/** 180 days — matches the consent-banner persistence window. */
export const CONSENT_MAX_AGE = 60 * 60 * 24 * 180;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.split('; ').find((c) => c.startsWith(name + '='));
  return m ? decodeURIComponent(m.slice(name.length + 1)) : null;
}

/** The visitor's currently-stored consent choice (null when none/invalid). */
export function readStoredConsent(): StoredConsent {
  return asStoredConsent(readCookie(CONSENT_COOKIE));
}

/** Persist a consent choice to the first-party `bizrnr_consent` cookie. */
export function writeStoredConsent(state: Exclude<StoredConsent, null>): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${CONSENT_COOKIE}=${state}; Max-Age=${CONSENT_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
}

/**
 * Push a granular Consent Mode v2 update. analytics_storage follows `analytics`;
 * the three advertising signals follow `ads`. functionality/security stay granted.
 */
export function applyConsent({ analytics, ads }: { analytics: boolean; ads: boolean }): void {
  if (typeof window === 'undefined') return;
  const consent = {
    ad_storage: ads ? 'granted' : 'denied',
    ad_user_data: ads ? 'granted' : 'denied',
    ad_personalization: ads ? 'granted' : 'denied',
    analytics_storage: analytics ? 'granted' : 'denied',
    functionality_storage: 'granted', // site functionality is always allowed
    security_storage: 'granted',
  };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'consent_update', consent });
  if (window.gtag) window.gtag('consent', 'update', consent);
}

/**
 * Nudge same-tab consent consumers to re-check the cookie. The analytics pixels
 * poll the cookie plus a `storage` listener, but `storage` does not fire in the
 * same tab that wrote the cookie — so dispatch it manually after a consent change
 * (e.g. from PrivacyChoices) to let pixels mount/unmount without a reload.
 */
export function notifyConsentChange(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('storage'));
}
